import { motion } from "framer-motion";
import { ClipboardCheck, TrendingUp, FileText, BrainCircuit } from "lucide-react";

const BENEFITS = [
  {
    icon: TrendingUp,
    title: "Student Progress",
    description:
      "Use the Student Progress Dashboard to review development, keep complete learning records and plan where to focus at the next lesson.",
    highlight: false,
  },
  {
    icon: ClipboardCheck,
    title: "Students and Lesson Continuity",
    description:
      "Keep student details, history and Student Packages organised. Structured RSA and DVSA assessments feed into a clear record of progress over time.",
    highlight: false,
  },
  {
    icon: FileText,
    title: "Professional Reports",
    description:
      "Generate professional branded PDF reports featuring your own logo, giving students clear, easy-to-understand feedback after every lesson.",
    highlight: false,
  },
  {
    icon: BrainCircuit,
    title: "Instructor Insight",
    description:
      "Spot recurring faults and weak areas so lesson planning is more direct and progress is easier to monitor over time.",
    highlight: true,
  },
];

const gridVariants = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12 } },
};
const cardVariants = {
  hidden: { opacity: 0, y: 28 },
  show: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
};

export const WhyMockTP = () => {
  return (
    <section
      id="features"
      data-testid="why-section"
      className="relative overflow-hidden scroll-mt-24"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute top-1/3 left-0 h-[400px] w-[500px] rounded-full bg-[#00DC82]/[0.04] blur-[130px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 py-24 lg:py-32">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 0.6 }}
          className="max-w-3xl mx-auto text-center flex flex-col gap-5"
        >
          <h2
            data-testid="why-title"
            className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tighter text-white leading-[1.1]"
          >
            Why instructors choose MockTP.
          </h2>
          <p
            data-testid="why-subtitle"
            className="text-base md:text-lg text-slate-400 leading-relaxed"
          >
            Built to help instructors manage student progress, deliver professional
            feedback and keep complete learning records.
          </p>
        </motion.div>

        {/* Benefits 2x2 grid */}
        <motion.div
          variants={gridVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6 max-w-5xl mx-auto"
        >
          {BENEFITS.map((b) => {
            const Icon = b.icon;
            return (
              <motion.div
                key={b.title}
                variants={cardVariants}
                data-testid={`benefit-card-${b.title.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`}
                className={`group relative flex items-start gap-5 rounded-2xl p-8 border transition-[transform,box-shadow,border-color,background-color] duration-300 hover:-translate-y-1.5 ${
                  b.highlight
                    ? "bg-[#00DC82]/[0.05] border-[#00DC82]/25 hover:border-[#00DC82]/50 hover:shadow-[0_20px_50px_-20px_rgba(0,220,130,0.35)]"
                    : "bg-white/[0.03] border-white/[0.07] hover:border-white/15 hover:bg-white/[0.05] hover:shadow-[0_20px_50px_-20px_rgba(0,0,0,0.7)]"
                }`}
              >
                <div
                  className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl transition-transform duration-300 group-hover:scale-110 ${
                    b.highlight
                      ? "bg-[#00DC82]/15 text-[#00DC82]"
                      : "bg-white/[0.05] text-[#00DC82]"
                  }`}
                >
                  <Icon size={24} strokeWidth={1.75} />
                </div>
                <div className="flex flex-col gap-2">
                  <h3 className="text-lg font-semibold tracking-tight text-white">
                    {b.title}
                  </h3>
                  <p className="text-sm text-slate-400 leading-relaxed">
                    {b.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Quote block */}
        <motion.figure
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.5 }}
          transition={{ duration: 0.7 }}
          data-testid="philosophy-quote"
          className="relative mt-24 rounded-3xl border border-white/[0.07] bg-white/[0.02] px-8 py-16 md:px-16 md:py-20 text-center overflow-hidden"
        >
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 h-[240px] w-[500px] rounded-full bg-[#00DC82]/[0.06] blur-[120px]" />
          </div>
          <div className="relative flex flex-col items-center gap-8">
            <span className="font-serif text-6xl leading-none text-[#00DC82]/40 select-none">
              &ldquo;
            </span>
            <blockquote className="max-w-4xl text-2xl md:text-4xl font-semibold tracking-tight text-white leading-snug">
              Built by instructors, for instructors.
            </blockquote>
            <figcaption className="text-sm font-medium tracking-wide text-[#00DC82]">
              MockTP
            </figcaption>
          </div>
        </motion.figure>
      </div>
    </section>
  );
};
